import { useCallback } from 'react';
import { Editor, Descendant, Node } from 'slate';
import { useSMSMetrics, SMSMetrics } from '../utils/use-sms-metrics';

interface UseEditorContentReturn {
  metrics: SMSMetrics;
  handleChange: (value: Descendant[]) => void;
  insertEmoji: (emoji: { native: string }) => void;
}

interface UseEditorContentProps {
  editor: Editor;
  onChange: (value: Descendant[]) => void;
}

/**
 * Custom hook to manage SMS editor content and metrics.
 * Follows Slate's conventions for document manipulation and state management.
 *
 * @param props - Hook configuration
 * @param props.editor - Slate Editor instance
 * @param props.onChange - Callback for raw editor state changes
 * @returns Object containing metrics and content manipulation methods
 */
export function useEditorContent({
  editor,
  onChange
}: UseEditorContentProps): UseEditorContentReturn {
  const { metrics, updateMetrics: debouncedSetMetrics } = useSMSMetrics();

  /**
   * Handles changes to the editor content
   */
  const handleChange = useCallback(
    (newValue: Descendant[]) => {
      try {
        // Extract plain text for metrics calculation
        const textContent = newValue.map((node) => Node.string(node)).join('\n');
        debouncedSetMetrics(textContent);

        // Pass through raw editor state
        onChange(newValue);
      } catch (e) {
        console.warn('Error in handleChange:', e);
      }
    },
    [onChange, debouncedSetMetrics]
  );

  /**
   * Inserts an emoji using Slate's transformation system.
   * Uses Transforms.insertText which is Slate's preferred way to insert text content.
   *
   * @param emoji - Emoji object containing the native emoji character
   */
  const insertEmoji = useCallback(
    (emoji: { native: string }) => {
      editor.insertText(emoji.native);
    },
    [editor]
  );

  return {
    metrics,
    handleChange,
    insertEmoji
  };
}
